import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { SubscriptionPlan } from '@/types/subscription-types';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import PlanCarouselCard from './PlanCarouselCard';
import { cn } from '@/lib/utils';

interface PlanCarouselContainerProps {
  plans: SubscriptionPlan[];
  currentPlanId: string | null;
  selectedPlan?: {name: string, price: number, tokens: number, id: string, priceId: string} | null;
  onSelectPlan: (planId: string, priceId: string) => void;
  onSelectedPlanUpdate?: (planName: string, planPrice: number, planTokens: number, planId?: string, priceId?: string) => void;
  isUpgrading?: boolean;
  renderActionExternal?: boolean;
}

const PlanCarouselContainer: React.FC<PlanCarouselContainerProps> = ({
  plans,
  currentPlanId,
  selectedPlan,
  onSelectPlan,
  onSelectedPlanUpdate,
  isUpgrading = false,
  renderActionExternal = false
}) => {
  const [localSelectedPlanId, setLocalSelectedPlanId] = useState<string | null>(null);
  
  // Find current plan
  const currentPlan = plans.find(p => p.id === currentPlanId);
  
  // Auto-select next tier up when plans or currentPlanId changes
  useEffect(() => {
    if (!plans.length || !currentPlanId || localSelectedPlanId !== null) return;
    
    // Sort plans by price to determine tiers
    const sortedPlans = [...plans].sort((a, b) => a.price - b.price);
    const currentPlanIndex = sortedPlans.findIndex(p => p.id === currentPlanId);
    
    if (currentPlanIndex !== -1 && currentPlanIndex < sortedPlans.length - 1) {
      // Select next tier up (higher price)
      const nextTierPlan = sortedPlans[currentPlanIndex + 1];
      setLocalSelectedPlanId(nextTierPlan.id);
      if (onSelectedPlanUpdate) {
        onSelectedPlanUpdate(nextTierPlan.name, nextTierPlan.price, nextTierPlan.aiTokens, nextTierPlan.id, nextTierPlan.priceId);
      }
    }
    // If user is on highest tier, don't auto-select anything (localSelectedPlanId stays null)
  }, [plans, currentPlanId, onSelectedPlanUpdate, localSelectedPlanId]);
  
  // Handle plan selection
  const handlePlanSelect = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    if (!plan) return;

    if (planId === currentPlanId) {
      // If clicking current plan, reset selection
      setLocalSelectedPlanId(null);
      if (onSelectedPlanUpdate) {
        onSelectedPlanUpdate('', 0, 0, '', '');
      }
    } else {
      // If clicking different plan, select it
      setLocalSelectedPlanId(planId);
      if (onSelectedPlanUpdate) {
        onSelectedPlanUpdate(plan.name, plan.price, plan.aiTokens, plan.id, plan.priceId);
      }
    }
  };

  // Handle final plan selection (upgrade/downgrade)
  const handleFinalPlanSelection = async () => {
    const selectedPlanData = plans.find(p => p.id === localSelectedPlanId);
    if (selectedPlanData && localSelectedPlanId !== currentPlanId) {
      await onSelectPlan(selectedPlanData.id, selectedPlanData.priceId);
    }
  };

  // Determine if we should show the upgrade button
  const shouldShowUpgradeButton = !!localSelectedPlanId && localSelectedPlanId !== currentPlanId;
  const selectedPlanData = plans.find(p => p.id === localSelectedPlanId);
  const isUpgrade = selectedPlanData && currentPlan && selectedPlanData.price > currentPlan.price;
  const isDowngrade = selectedPlanData && currentPlan && selectedPlanData.price < currentPlan.price;
  const isDowngradeToFree = isDowngrade && selectedPlanData?.price === 0;

  // Mark the most popular plan (AI Leader instead of middle-tier)
  const getPopularPlan = () => {
    // Look for Leader plan first (with or without AI prefix)
    const leaderPlan = plans.find(plan => 
      plan.name.toLowerCase().includes('leader')
    );
    
    if (leaderPlan) {
      return leaderPlan.id;
    }
    
    // Fallback to middle-tier plan if AI Leader not found
    const sortedPlans = [...plans].sort((a, b) => a.price - b.price);
    const middleIndex = Math.floor(sortedPlans.length / 2);
    return sortedPlans[middleIndex]?.id;
  };

  const popularPlanId = getPopularPlan();

  // Handle empty plans
  if (!plans || plans.length === 0) {
    return (
      <div className="w-full text-center py-12">
        <div className="text-gray-500 dark:text-gray-400">
          <p className="text-lg font-medium mb-2">No plans available</p>
          <p className="text-sm">Please check back later or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Simplified Carousel with space for external arrows and hover expansion */}
      <div className="mx-0 sm:mx-20 py-0 sm:py-4">
        <Carousel
          opts={{
            align: "start",
            loop: false,
            skipSnaps: false,
            dragFree: true,
            containScroll: "trimSnaps",
          }}
          className="w-full relative"
        >
        <CarouselContent className="ml-2">
          {plans.map((plan) => (
            <CarouselItem
              key={plan.id}
              className={cn(
                "px-2 py-0 sm:py-4",  // Added horizontal and vertical padding for hover expansion
                // Responsive sizing - optimized for 4 cards
                "basis-full",                    // Mobile: 1 card
                "sm:basis-1/2",                  // Small: 2 cards  
                "lg:basis-1/3",                  // Large: 3 cards
                "xl:basis-1/4"                   // XL: 4 cards (all visible)
              )}
            >
              <div className="h-[472px] relative z-10">
                <PlanCarouselCard
                  plan={plan}
                  isCurrentPlan={plan.id === currentPlanId}
                  isSelectedPlan={plan.id === localSelectedPlanId}
                  onSelectPlan={handlePlanSelect}
                  isPopular={plan.id === popularPlanId}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        
        {/* Arrows positioned outside the main container */}
        <div className="hidden sm:block">
          <CarouselPrevious className="absolute -left-16 top-1/2 -translate-y-1/2 h-12 w-12 bg-white dark:bg-gray-800 shadow-xl border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110 transition-all duration-200" />
          <CarouselNext className="absolute -right-16 top-1/2 -translate-y-1/2 h-12 w-12 bg-white dark:bg-gray-800 shadow-xl border-2 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:scale-110 transition-all duration-200" />
        </div>
        </Carousel>
      </div>

      {/* Selection Summary & Upgrade Button */}
      {!renderActionExternal && (
        <AnimatePresence>
          {shouldShowUpgradeButton && selectedPlanData && (
          <motion.div
            initial={{ opacity: 0, y: 20, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: 20, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="mt-4 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 py-6 px-8 w-fit">
              <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                {/* Selection Summary */}
                <div className="flex items-center space-x-4">
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center",
                    isUpgrade ? "bg-gradient-to-r from-green-500 to-green-600" :
                    isDowngrade ? "bg-gradient-to-r from-orange-500 to-orange-600" :
                    "bg-gradient-to-r from-blue-500 to-blue-600"
                  )}>
                    {isUpgrade ? (
                      <ArrowUp className="w-6 h-6 text-white" />
                    ) : (
                      <ArrowDown className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isUpgrade ? 'Upgrade' : 'Change'} to {selectedPlanData.name.replace(/^AI /, '')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedPlanData.price === 0 ? 'Free' : `$${selectedPlanData.price}/month`} <span className="text-xs opacity-60">USD</span> • {selectedPlanData.aiTokens.toLocaleString()} Credits
                    </p>
                  </div>
                </div>

                {/* Action Button */}
                <motion.button
                  onClick={handleFinalPlanSelection}
                  disabled={isUpgrading}
                  className={cn(
                    "px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center space-x-2",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500",
                    "hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed",
                    isUpgrading && "animate-pulse",
                    isUpgrade
                      ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-green-500/25"
                      : isDowngradeToFree
                        ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-red-500/25"
                        : isDowngrade
                          ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:shadow-orange-500/25"
                          : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-blue-500/25"
                  )}
                  whileTap={{ scale: isUpgrading ? 1 : 0.98 }}
                >
                  {isUpgrading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {isUpgrade ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                      <span>
                        {isDowngradeToFree
                          ? 'Cancel Plan'
                          : isDowngrade
                            ? `Downgrade to ${selectedPlanData.name.replace(/^AI /, '')}`
                            : `${isUpgrade ? 'Upgrade' : 'Change'} to ${selectedPlanData.name.replace(/^AI /, '')}`
                        }
                      </span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Mobile Navigation Hint */}
      <div className="sm:hidden mt-4 pb-8 text-center">
        <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
          <span>◄</span>
          <span>Swipe left or right to see all plans</span>
          <span>►</span>
        </p>
      </div>
    </div>
  );
};

// Export action button component for external rendering
export const PlanActionButton: React.FC<{
  selectedPlanData: SubscriptionPlan | undefined;
  currentPlanId: string | null;
  isUpgrading: boolean;
  onSelectPlan: (planId: string, priceId: string) => void;
  plans: SubscriptionPlan[];
}> = ({ selectedPlanData, currentPlanId, isUpgrading, onSelectPlan, plans }) => {
  const currentPlan = plans.find(p => p.id === currentPlanId);
  const isCurrentPlanSelected = selectedPlanData?.id === currentPlanId || !selectedPlanData;
  const isUpgrade = selectedPlanData && currentPlan && selectedPlanData.price > currentPlan.price;
  const isDowngrade = selectedPlanData && currentPlan && selectedPlanData.price < currentPlan.price;
  const isDowngradeToFree = isDowngrade && selectedPlanData?.price === 0;

  const handleFinalPlanSelection = async () => {
    if (selectedPlanData && !isCurrentPlanSelected) {
      await onSelectPlan(selectedPlanData.id, selectedPlanData.priceId);
    }
  };

  // Always show the card, matching AI Usage container structure
  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 py-7 px-8">
      <div className="flex flex-col md:flex-row items-center justify-center gap-6">
        
        {isCurrentPlanSelected ? (
          // Show current plan info when no plan is selected or current plan is selected
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center">
              <ArrowUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentPlan?.name || 'Current Plan'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {currentPlan?.price === 0 ? 'Free' : `$${currentPlan?.price}/month`} <span className="text-xs opacity-60">USD</span> • {currentPlan?.aiTokens.toLocaleString()} Credits
              </p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-lg text-sm font-medium">
              ✓ Active Plan
            </div>
          </div>
        ) : (
          // Show upgrade/change info when different plan is selected
          <>
            <div className="flex items-center space-x-4">
              <div className={cn(
                "w-12 h-12 rounded-xl flex items-center justify-center",
                isUpgrade ? "bg-gradient-to-r from-green-500 to-green-600" :
                isDowngrade ? "bg-gradient-to-r from-orange-500 to-orange-600" :
                "bg-gradient-to-r from-blue-500 to-blue-600"
              )}>
                {isUpgrade ? (
                  <ArrowUp className="w-6 h-6 text-white" />
                ) : (
                  <ArrowDown className="w-6 h-6 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isUpgrade ? 'Upgrade' : 'Change'} to {selectedPlanData?.name.replace(/^AI /, '')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedPlanData?.price === 0 ? 'Free' : `$${selectedPlanData?.price}/month`} <span className="text-xs opacity-60">USD</span> • {selectedPlanData?.aiTokens.toLocaleString()} Credits
                </p>
              </div>
            </div>

            {/* Action Button */}
            <motion.button
              onClick={handleFinalPlanSelection}
              disabled={isUpgrading}
              className={cn(
                "px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center space-x-2",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500",
                "hover:shadow-lg disabled:opacity-75 disabled:cursor-not-allowed",
                isUpgrading && "animate-pulse",
                isUpgrade
                  ? "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:shadow-green-500/25"
                  : isDowngradeToFree
                    ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 hover:shadow-red-500/25"
                    : isDowngrade
                      ? "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:shadow-orange-500/25"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 hover:shadow-blue-500/25"
              )}
              whileTap={{ scale: isUpgrading ? 1 : 0.98 }}
            >
              {isUpgrading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  {isUpgrade ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                  <span>
                    {isDowngradeToFree
                      ? 'Cancel Plan'
                      : isDowngrade
                        ? `Downgrade to ${selectedPlanData?.name.replace(/^AI /, '')}`
                        : `${isUpgrade ? 'Upgrade' : 'Change'} to ${selectedPlanData?.name.replace(/^AI /, '')}`
                    }
                  </span>
                </>
              )}
            </motion.button>
          </>
        )}
      </div>
    </div>
    </div>
  );
};

export default PlanCarouselContainer;